import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useUscgFocRequirementsFlag } from 'utils/featureFlags';
import {
  assetsInCone,
  formatUscgFocAssetLabel,
} from './uscgFocSoutheastTropicalData';
import {
  USCG_FOC_SE_EVENT_SUMMARY,
  USCG_FOC_SE_LINKED_EVENT_NAME,
  USCG_FOC_SE_ORG_EVENT_DEMO_KEY,
  USCG_FOC_SE_THRESHOLD_DESCRIPTION,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/uscgFocSoutheastThreatNotification';
import {
  orgEventKeys,
  type OrgEvent,
} from 'hooks/orgEvents/orgEventHooks';

const ensureLocks = new Map<string, Promise<unknown>>();

function coneCentroid(): { lon: number; lat: number } {
  const assets = assetsInCone();
  if (assets.length === 0) {
    return { lon: -82.5, lat: 28.5 };
  }
  const lon = assets.reduce((sum, asset) => sum + asset.lon, 0) / assets.length;
  const lat = assets.reduce((sum, asset) => sum + asset.lat, 0) / assets.length;
  return { lon, lat };
}

function resourcesCommittedSummary(): string {
  const assets = assetsInCone();
  if (assets.length === 0) {
    return 'No USCG assets currently in the Helene forecast cone.';
  }
  const preview = assets
    .slice(0, 5)
    .map((asset) => formatUscgFocAssetLabel(asset))
    .join('; ');
  const more = assets.length > 5 ? ` (+${assets.length - 5} more)` : '';
  return `${assets.length} USCG Southeast District assets in forecast cone: ${preview}${more}`;
}

async function patchUscgFocSeOrgEventIfStale(
  existing: OrgEvent,
  queryClient: ReturnType<typeof useQueryClient>
): Promise<OrgEvent> {
  const resourcesCommitted = resourcesCommittedSummary();
  const needsPatch =
    existing.name !== USCG_FOC_SE_LINKED_EVENT_NAME ||
    existing.summary !== USCG_FOC_SE_EVENT_SUMMARY ||
    existing.resources_committed !== resourcesCommitted ||
    existing.region !== 'Southeast District' ||
    existing.type !== 'Tropical Storm' ||
    existing.threshold_description !== USCG_FOC_SE_THRESHOLD_DESCRIPTION;
  if (!needsPatch) {
    return existing;
  }

  const patched = await axios.patch<OrgEvent>(
    `${config.apiGateway.org_events}/${existing.id}`,
    {
      name: USCG_FOC_SE_LINKED_EVENT_NAME,
      summary: USCG_FOC_SE_EVENT_SUMMARY,
      resources_committed: resourcesCommitted,
      region: 'Southeast District',
      type: 'Tropical Storm',
      threshold_description: USCG_FOC_SE_THRESHOLD_DESCRIPTION,
    }
  );
  await queryClient.invalidateQueries({
    queryKey: orgEventKeys.list(existing.group_guid),
  });
  return patched.data;
}

async function ensureUscgFocSeOrgEventForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<OrgEvent> {
  const resp = await axios.get<OrgEvent[]>(config.apiGateway.org_events, {
    params: { groupGuid },
  });
  const existing = (resp.data ?? []).find(
    (event) => event.demo_key === USCG_FOC_SE_ORG_EVENT_DEMO_KEY
  );
  if (existing) {
    return patchUscgFocSeOrgEventIfStale(existing, queryClient);
  }

  const { lon, lat } = coneCentroid();
  const created = await axios.post<OrgEvent>(config.apiGateway.org_events, {
    group_guid: groupGuid,
    name: USCG_FOC_SE_LINKED_EVENT_NAME,
    type: 'Tropical Storm',
    status: 'Active',
    severity: 'High',
    region: 'Southeast District',
    business_unit: 'United States Coast Guard',
    summary: USCG_FOC_SE_EVENT_SUMMARY,
    resources_committed: resourcesCommittedSummary(),
    lon,
    lat,
    started_at: '2024-09-24T12:00:00.000Z',
    creation_kind: 'threshold',
    created_by_user_guid: userGuid,
    threshold_description: USCG_FOC_SE_THRESHOLD_DESCRIPTION,
    demo_key: USCG_FOC_SE_ORG_EVENT_DEMO_KEY,
  });

  await queryClient.invalidateQueries({
    queryKey: orgEventKeys.list(groupGuid),
  });

  return created.data;
}

/**
 * When FOC is on, ensure the Southeast tropical org event exists (idempotent
 * via demo_key).
 */
export function useEnsureUscgFocSeOrgEvent() {
  const focEnabled = useUscgFocRequirementsFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!focEnabled || !groupGuid || !userGuid) {
      return;
    }
    if (completedRef.current === groupGuid) {
      return;
    }

    const lockKey = `uscg-foc-se-org-event:${groupGuid}`;
    let cancelled = false;

    const run = async () => {
      const existing = ensureLocks.get(lockKey);
      if (existing) {
        await existing;
        if (!cancelled) completedRef.current = groupGuid;
        return;
      }

      const promise = ensureUscgFocSeOrgEventForGroup({
        groupGuid,
        userGuid,
        queryClient,
      }).finally(() => {
        ensureLocks.delete(lockKey);
      });
      ensureLocks.set(lockKey, promise);

      try {
        await promise;
        if (!cancelled) completedRef.current = groupGuid;
      } catch (error) {
        console.error('Failed to ensure USCG FOC Southeast org event', error);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [focEnabled, groupGuid, userGuid, queryClient]);
}
